import { type PropsWithChildren } from 'react'
import { type SystemCssProperties } from '@dhl-ui/theme'
import { type TypographyProps } from '../Typography'
import { BaseTypography } from '../Typography/BaseTypography'

export type EllipsisProps = PropsWithChildren<{
  /**
   * Max number of lines with text
   * @default 1
   */
  lineClamp?: number

  /**
   * The text variant
   * @default body1
   */
  variant?: TypographyProps['variant']

  /**
   * We are breaking by word but you can also break by all
   * @default word
   */
  breakOn?: 'word' | 'all'

  color?: TypographyProps['color']

  /**
   * The alternative component (H1,H2,etc)
   * @default div
   */
  component?: TypographyProps['component']

  sx?: Pick<SystemCssProperties, 'flex' | 'lineHeight'>
}>

/**
 * Ellipsis
 *
 * Demos:
 * - [Ellipsis](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/primitives-ellipsis--docs)
 *
 */

export const Ellipsis = ({
  children,
  variant = 'body1',
  color,
  component = 'div',
  lineClamp = 1,
  breakOn = 'word',
  sx,
}: EllipsisProps) => (
  <BaseTypography
    variant={variant}
    component={component}
    color={color}
    sx={{
      ...sx,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: lineClamp,
      WebkitBoxOrient: 'vertical',
      wordBreak: `break-${breakOn}`,
    }}
  >
    {children}
  </BaseTypography>
)
